import { useSystemHealth } from "@src/presentation/hooks/system";
import { Card, CardBody, CardHeader, Spinner, Button } from "@heroui/react";
import { Activity, AlertCircle, CheckCircle2, RefreshCw, Server, XCircle, Database } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";

export default function SystemHealthPage() {
    const { data: health, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useSystemHealth({
        refetchInterval: 30000, // Auto-refresh every 30 seconds
    });

    // Loading state
    if (isLoading) {
        return (
            <div className="flex h-[60vh] items-center justify-center">
                <div className="text-center">
                    <Spinner size="lg" color="primary" />
                    <p className="mt-4 text-foreground-500">Checking system health...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (isError || !health) {
        return (
            <div className="container mx-auto max-w-4xl space-y-6 p-4">
                <Card className="rounded border border-danger/20 shadow-none">
                    <CardBody className="text-center p-8">
                        <div className="mb-4 flex justify-center">
                            <div className="rounded-full bg-danger/10 p-3">
                                <AlertCircle className="h-8 w-8 text-danger" />
                            </div>
                        </div>
                        <h3 className="mb-2 text-lg font-semibold">Health Check Failed</h3>
                        <p className="mb-4 text-sm text-foreground-500">
                            {error?.message || "Unable to retrieve system health information"}
                        </p>
                        <Button
                            color="primary"
                            onPress={() => refetch()}
                            isLoading={isFetching}
                            startContent={!isFetching ? <RefreshCw className="h-4 w-4" /> : undefined}
                            className="rounded"
                        >
                            Retry
                        </Button>
                    </CardBody>
                </Card>
            </div>
        );
    }

    const services = Object.entries(health.services);
    const allHealthy = health.status === "healthy";
    const healthyCount = health.getHealthyCount();
    const unhealthyCount = health.getUnhealthyCount();
    const totalServices = health.getTotalServices();

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row h-auto sm:h-16 py-3 sm:py-0 items-start sm:items-center justify-between gap-3 sm:gap-0">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                                <Activity className="h-4 sm:h-5 w-4 sm:w-5 text-primary-foreground" />
                            </div>
                            <h1 className="text-lg sm:text-xl font-semibold text-foreground">System Health</h1>
                        </div>
                        <Button
                            variant="bordered"
                            onPress={() => refetch()}
                            isLoading={isFetching}
                            startContent={!isFetching ? <RefreshCw className="h-4 w-4" /> : undefined}
                            className="rounded w-full sm:w-auto"
                            size="sm"
                        >
                            Refresh
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Overall Status Card */}
                <Card className="rounded border border-border shadow-none">
                    <CardBody className="p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
                            <div className="flex items-center gap-3 sm:gap-4">
                                <div
                                    className={`flex h-12 sm:h-16 w-12 sm:w-16 items-center justify-center rounded-lg ${allHealthy ? "bg-success/10" : "bg-danger/10"
                                        }`}
                                >
                                    {allHealthy ? (
                                        <CheckCircle2 className="h-6 sm:h-8 w-6 sm:w-8 text-success" />
                                    ) : (
                                        <XCircle className="h-6 sm:h-8 w-6 sm:w-8 text-danger" />
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs sm:text-sm text-muted-foreground">Overall Status</p>
                                    <h2
                                        className={`text-xl sm:text-2xl font-bold ${allHealthy ? "text-success" : "text-danger"
                                            }`}
                                    >
                                        {health.status.charAt(0).toUpperCase() + health.status.slice(1)}
                                    </h2>
                                    <p className="text-xs sm:text-sm text-muted-foreground">
                                        Last checked: {formatDistanceToNow(new Date(dataUpdatedAt), { addSuffix: true })}
                                    </p>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center w-full sm:w-auto">
                                <div>
                                    <p className="text-lg sm:text-2xl font-bold text-foreground">{totalServices}</p>
                                    <p className="text-xs text-muted-foreground">Total Services</p>
                                </div>
                                <div>
                                    <p className="text-lg sm:text-2xl font-bold text-success">{healthyCount}</p>
                                    <p className="text-xs text-muted-foreground">Healthy</p>
                                </div>
                                <div>
                                    <p className="text-lg sm:text-2xl font-bold text-danger">{unhealthyCount}</p>
                                    <p className="text-xs text-muted-foreground">Unhealthy</p>
                                </div>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                {/* Services Grid */}
                <div>
                    <h2 className="mb-4 text-lg font-semibold text-foreground">Service Details</h2>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {services.map(([serviceName, service]) => {
                            const isHealthy = service.status === "healthy";

                            return (
                                <Card
                                    key={serviceName}
                                    className={`rounded border shadow-none ${isHealthy ? "border-success/20 bg-success/5" : "border-danger/20 bg-danger/5"
                                        }`}
                                >
                                    <CardHeader className="border-b border-border bg-background/50">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className={`flex h-10 w-10 items-center justify-center rounded-lg ${isHealthy ? "bg-success/10" : "bg-danger/10"
                                                    }`}
                                            >
                                                <Server
                                                    className={`h-5 w-5 ${isHealthy ? "text-success" : "text-danger"
                                                        }`}
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-foreground capitalize">
                                                    {serviceName}
                                                </h3>
                                                <p
                                                    className={`text-sm ${isHealthy ? "text-success" : "text-danger"
                                                        }`}
                                                >
                                                    {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                                                </p>
                                            </div>
                                            {isHealthy ? (
                                                <CheckCircle2 className="h-5 w-5 text-success" />
                                            ) : (
                                                <XCircle className="h-5 w-5 text-danger" />
                                            )}
                                        </div>
                                    </CardHeader>
                                    {service.message && (
                                        <CardBody className="p-4">
                                            <div className="rounded-lg bg-background/50 p-3">
                                                <p className="text-xs font-medium text-muted-foreground mb-1">
                                                    Error Message:
                                                </p>
                                                <p className="text-sm text-foreground break-words font-mono">
                                                    {service.message}
                                                </p>
                                            </div>
                                        </CardBody>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                </div>

                {/* Database Pool Section */}
                {health.databasePool && (
                    <div>
                        <h2 className="mb-4 text-lg font-semibold text-foreground">Database Connection Pool</h2>
                        <Card className={`rounded border shadow-none ${health.databasePool.status === "healthy" ? "border-success/20 bg-success/5" : "border-danger/20 bg-danger/5"
                            }`}>
                            <CardHeader className="border-b border-border bg-background/50">
                                <div className="flex items-center gap-3">
                                    <div
                                        className={`flex h-10 w-10 items-center justify-center rounded-lg ${health.databasePool.status === "healthy" ? "bg-success/10" : "bg-danger/10"
                                            }`}
                                    >
                                        <Database
                                            className={`h-5 w-5 ${health.databasePool.status === "healthy" ? "text-success" : "text-danger"
                                                }`}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-foreground">Connection Pool</h3>
                                        <p
                                            className={`text-sm ${health.databasePool.status === "healthy" ? "text-success" : "text-danger"
                                                }`}
                                        >
                                            {health.databasePool.status.charAt(0).toUpperCase() + health.databasePool.status.slice(1)}
                                        </p>
                                    </div>
                                    {health.databasePool.status === "healthy" ? (
                                        <CheckCircle2 className="h-5 w-5 text-success" />
                                    ) : (
                                        <XCircle className="h-5 w-5 text-danger" />
                                    )}
                                </div>
                            </CardHeader>
                            <CardBody className="p-6">
                                <div className="space-y-4">
                                    {/* Utilization Bar */}
                                    <div>
                                        <div className="mb-2 flex items-center justify-between">
                                            <span className="text-sm font-medium text-foreground">Utilization</span>
                                            <span className="text-sm font-semibold text-foreground">
                                                {health.databasePool.utilization_percent}%
                                            </span>
                                        </div>
                                        <div className="h-2 w-full overflow-hidden rounded-full bg-background">
                                            <div
                                                className={`h-full transition-all ${health.databasePool.utilization_percent > 80
                                                    ? "bg-danger"
                                                    : health.databasePool.utilization_percent > 50
                                                        ? "bg-warning"
                                                        : "bg-success"
                                                    }`}
                                                style={{ width: `${health.databasePool.utilization_percent}%` }}
                                            />
                                        </div>
                                    </div>

                                    {/* Statistics Grid */}
                                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
                                        <div className="rounded-lg bg-background/50 p-3">
                                            <p className="text-xs text-muted-foreground">Pool Size</p>
                                            <p className="mt-1 text-xl font-bold text-foreground">
                                                {health.databasePool.statistics.size}
                                            </p>
                                        </div>
                                        <div className="rounded-lg bg-background/50 p-3">
                                            <p className="text-xs text-muted-foreground">Checked Out</p>
                                            <p className="mt-1 text-xl font-bold text-foreground">
                                                {health.databasePool.statistics.checked_out}
                                            </p>
                                        </div>
                                        <div className="rounded-lg bg-background/50 p-3">
                                            <p className="text-xs text-muted-foreground">Checked In</p>
                                            <p className="mt-1 text-xl font-bold text-foreground">
                                                {health.databasePool.statistics.checked_in}
                                            </p>
                                        </div>
                                        <div className="rounded-lg bg-background/50 p-3">
                                            <p className="text-xs text-muted-foreground">Total Connections</p>
                                            <p className="mt-1 text-xl font-bold text-foreground">
                                                {health.databasePool.statistics.total}
                                            </p>
                                        </div>
                                        <div className="rounded-lg bg-background/50 p-3">
                                            <p className="text-xs text-muted-foreground">Overflow</p>
                                            <p className={`mt-1 text-xl font-bold ${health.databasePool.statistics.overflow > 0 ? "text-danger" : "text-foreground"
                                                }`}>
                                                {health.databasePool.statistics.overflow}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                )}

                {/* Timestamp Card */}
                <Card className="rounded border border-border shadow-none bg-muted/50">
                    <CardBody className="p-4">
                        <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <Activity className="h-4 w-4" />
                                <span>Health check timestamp</span>
                            </div>
                            <span className="font-medium text-foreground">
                                {format(health.timestamp, "PPpp")}
                            </span>
                        </div>
                    </CardBody>
                </Card>

                {/* Info Card */}
                <Card className="rounded border border-primary/20 bg-primary/5 shadow-none">
                    <CardBody className="p-4">
                        <div className="flex gap-3">
                            <AlertCircle className="h-5 w-5 shrink-0 text-primary mt-0.5" />
                            <div className="text-sm">
                                <p className="font-medium text-primary">Automatic Monitoring</p>
                                <p className="mt-1 text-foreground-600">
                                    This page automatically refreshes every 30 seconds to keep you updated on the
                                    system's health status. You can also manually refresh using the button above.
                                </p>
                            </div>
                        </div>
                    </CardBody>
                </Card>
            </main>
        </div>
    );
}
